import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  alpha,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import {
  buildFindReplaceValue,
  getFindReplaceRegexError,
  type FindReplaceOptions,
  type SearchMatch,
} from '../../lib/utils/findReplaceUtils';
import { borderRadius, getControlBorderColor, getFieldBorderColor, layoutGap } from '../../../theme/theme';
import { FindReplaceDialogActions } from './FindReplaceDialogActions';
import { FindReplaceDialogHeader } from './FindReplaceDialogHeader';
import { previewDockPaperSx, previewDockSx } from './previewDockSx';

const MATCH_PREVIEW_LIMIT = 24;
export type { FindReplaceOptions, SearchMatch } from '../../lib/utils/findReplaceUtils';

export interface FindReplaceDialogProps {
  open: boolean;
  onClose: () => void;
  columns: string[];
  visibleRowIndices?: number[];
  selectedRowIndices?: number[];
  onFind: (searchTerm: string, options: FindReplaceOptions) => SearchMatch[];
  onReplace: (match: SearchMatch, replaceValue: string, searchTerm: string, options: FindReplaceOptions) => void | Promise<void>;
  onReplaceAll: (searchTerm: string, replaceValue: string, options: FindReplaceOptions) => number | Promise<number>;
  onHighlightMatch?: (match: SearchMatch | null) => void;
  /** Tour / read-only preview: block close actions and primary edits */
  previewOnly?: boolean;
  contentRef?: React.RefObject<HTMLDivElement | null>;
}

const FindReplaceDialog: React.FC<FindReplaceDialogProps> = ({
  open,
  onClose,
  columns,
  visibleRowIndices,
  selectedRowIndices,
  onFind,
  onReplace,
  onReplaceAll,
  onHighlightMatch,
  previewOnly = false,
  contentRef,
}) => {
  const theme = useTheme();
  const surfaceBorder = alpha(theme.palette.common.white, 0.06);
  const mutedSurface = alpha(theme.palette.common.white, 0.02);
  const raisedSurface = alpha(theme.palette.common.white, 0.04);
  const controlBorderColor = getControlBorderColor(theme);
  const fieldBorderColor = getFieldBorderColor(theme);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [options, setOptions] = useState<FindReplaceOptions>({
    caseSensitive: false,
    wholeCell: false,
    scope: 'all',
    scopeColumn: undefined,
    useRegularExpression: false,
    rowScope: 'all',
    rowIndices: undefined,
  });

  // Results state
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchDirty, setSearchDirty] = useState(false);
  const [replaceCount, setReplaceCount] = useState<number | null>(null);
  const [isReplacingCurrent, setIsReplacingCurrent] = useState(false);
  const [isReplacingAll, setIsReplacingAll] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      setHasSearched(false);
      setSearchDirty(false);
      setReplaceCount(null);
      setIsReplacingCurrent(false);
      setIsReplacingAll(false);
    }
  }, [open]);

  // Keep column-scoped searches aligned with the available editable columns.
  const editableColumns = React.useMemo(
    () => columns.filter(col => col !== 'Name'),
    [columns]
  );
  const safeVisibleRowIndices = React.useMemo(
    () => Array.isArray(visibleRowIndices) ? visibleRowIndices : [],
    [visibleRowIndices]
  );
  const safeSelectedRowIndices = React.useMemo(
    () => Array.isArray(selectedRowIndices) ? selectedRowIndices : [],
    [selectedRowIndices]
  );

  useEffect(() => {
    if (!open || options.scope !== 'column') {
      return;
    }

    if (options.scopeColumn && editableColumns.includes(options.scopeColumn)) {
      return;
    }

    const fallbackColumn = editableColumns[0];
    if (!fallbackColumn) {
      return;
    }

    setOptions((prev) => {
      if (prev.scope !== 'column' || prev.scopeColumn === fallbackColumn) {
        return prev;
      }

      return {
        ...prev,
        scopeColumn: fallbackColumn,
      };
    });
  }, [editableColumns, open, options.scope, options.scopeColumn]);

  useEffect(() => {
    if (!open || options.rowScope !== 'selected' || safeSelectedRowIndices.length > 0) {
      return;
    }

    setOptions((prev) => ({ ...prev, rowScope: 'all', rowIndices: undefined }));
  }, [open, options.rowScope, safeSelectedRowIndices.length]);

  const hasSearchTerm = searchTerm.trim().length > 0;
  const effectiveOptions: FindReplaceOptions = React.useMemo(() => {
    const rowScope = options.rowScope ?? 'all';
    const rowIndices = rowScope === 'visible'
      ? safeVisibleRowIndices
      : rowScope === 'selected'
        ? safeSelectedRowIndices
        : undefined;

    return {
      ...options,
      rowScope,
      rowIndices,
      ...(options.scope === 'column'
        ? { scopeColumn: options.scopeColumn ?? editableColumns[0] }
        : {}),
    };
  }, [editableColumns, options, safeSelectedRowIndices, safeVisibleRowIndices]);
  const regexError = React.useMemo(
    () => getFindReplaceRegexError(searchTerm, effectiveOptions),
    [effectiveOptions, searchTerm]
  );
  const canRunSearch = hasSearchTerm && !regexError;

  const invalidateResults = useCallback(() => {
    setReplaceCount(null);

    if (!hasSearched) {
      return;
    }

    setSearchDirty(true);
    setMatches([]);
    setCurrentMatchIndex(-1);
    onHighlightMatch?.(null);
  }, [hasSearched, onHighlightMatch]);

  const hasActiveMatches = !searchDirty && matches.length > 0;
  const canReplaceCurrent = hasActiveMatches && currentMatchIndex >= 0 && currentMatchIndex < matches.length;
  const canReplaceAll = canRunSearch;
  const isReplacing = isReplacingCurrent || isReplacingAll;
  const currentMatch = canReplaceCurrent ? matches[currentMatchIndex] ?? null : null;
  const previewMatches = React.useMemo(() => {
    if (!hasActiveMatches) {
      return [];
    }

    const indexedMatches = matches.map((match, index) => ({ match, index }));
    if (indexedMatches.length <= MATCH_PREVIEW_LIMIT) {
      return indexedMatches;
    }

    if (currentMatchIndex >= MATCH_PREVIEW_LIMIT && currentMatchIndex < indexedMatches.length) {
      return [
        ...indexedMatches.slice(0, MATCH_PREVIEW_LIMIT - 1),
        indexedMatches[currentMatchIndex],
      ];
    }

    return indexedMatches.slice(0, MATCH_PREVIEW_LIMIT);
  }, [currentMatchIndex, hasActiveMatches, matches]);
  const hiddenMatchCount = hasActiveMatches ? Math.max(0, matches.length - previewMatches.length) : 0;
  const currentLocationText = currentMatch
    ? `Row ${currentMatch.row + 1} - ${currentMatch.column}`
    : null;
  const getMatchPreviewValue = useCallback(
    (match: SearchMatch) =>
      buildFindReplaceValue(match.value, searchTerm, replaceTerm, effectiveOptions),
    [effectiveOptions, replaceTerm, searchTerm]
  );

  const statusText = regexError
    ? 'Invalid regular expression'
    : !hasSearchTerm
    ? 'Enter a value to search'
    : replaceCount !== null
      ? `${replaceCount} replacement${replaceCount === 1 ? '' : 's'}`
    : !hasSearched
      ? 'Press Enter or Find all to search'
    : searchDirty
        ? 'Search changed. Press Enter or Find all to refresh.'
        : matches.length === 0
          ? 'No matches found'
          : `${currentMatchIndex + 1} of ${matches.length}`;

  const statusColor = regexError
    ? theme.palette.error.main
    : searchDirty
    ? theme.palette.warning.main
    : replaceCount !== null
      ? (replaceCount > 0 ? theme.palette.success.main : theme.palette.text.secondary)
      : matches.length > 0 && !searchDirty
        ? theme.palette.success.main
        : theme.palette.text.secondary;

  // Minimal input styling
  const inputSx = {
    mt: 0.75,
    overflow: 'visible',
    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: `${borderRadius.md}px`,
      backgroundColor: mutedSurface,
      fontSize: '0.8125rem',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: fieldBorderColor,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: fieldBorderColor,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: fieldBorderColor,
        borderWidth: 1,
      },
    },
  };

  const selectSx = {
    fontSize: '0.8125rem',
    borderRadius: `${borderRadius.md}px`,
    backgroundColor: mutedSurface,
    '& .MuiSelect-select': {
      py: 0.75,
      pr: '28px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: fieldBorderColor,
      borderWidth: 1,
    },
  };

  const optionToggleSx = {
    height: 30,
    px: 1.15,
    color: theme.palette.text.secondary,
    borderColor: controlBorderColor,
    fontSize: '0.72rem',
    fontWeight: 700,
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: raisedSurface,
      borderColor: controlBorderColor,
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      borderColor: controlBorderColor,
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.18),
        borderColor: controlBorderColor,
      },
    },
  };

  // Handle search
  const handleFind = useCallback(() => {
    if (!canRunSearch) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      setHasSearched(hasSearchTerm);
      setSearchDirty(false);
      onHighlightMatch?.(null);
      return;
    }

    const results = onFind(searchTerm, effectiveOptions);
    setHasSearched(true);
    setSearchDirty(false);
    setMatches(results);
    setReplaceCount(null);

    if (results.length > 0) {
      setCurrentMatchIndex(0);
      onHighlightMatch?.(results[0]);
    } else {
      setCurrentMatchIndex(-1);
      onHighlightMatch?.(null);
    }
  }, [canRunSearch, hasSearchTerm, searchTerm, effectiveOptions, onFind, onHighlightMatch]);

  // Handle find next
  const handleFindNext = useCallback(() => {
    if (searchDirty || matches.length === 0) {
      handleFind();
      return;
    }

    const nextIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIndex);
    onHighlightMatch?.(matches[nextIndex]);
  }, [matches, currentMatchIndex, handleFind, onHighlightMatch, searchDirty]);

  // Handle find previous
  const handleFindPrevious = useCallback(() => {
    if (searchDirty || matches.length === 0) {
      handleFind();
      return;
    }

    const prevIndex = currentMatchIndex <= 0 ? matches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    onHighlightMatch?.(matches[prevIndex]);
  }, [matches, currentMatchIndex, handleFind, onHighlightMatch, searchDirty]);

  const handleSelectMatch = useCallback((matchIndex: number) => {
    if (searchDirty || matchIndex < 0 || matchIndex >= matches.length) {
      return;
    }

    setCurrentMatchIndex(matchIndex);
    onHighlightMatch?.(matches[matchIndex]);
  }, [matches, onHighlightMatch, searchDirty]);

  // Handle replace current
  const handleReplaceCurrent = useCallback(async () => {
    if (!canRunSearch || currentMatchIndex < 0 || currentMatchIndex >= matches.length || searchDirty || isReplacingCurrent) return;

    setIsReplacingCurrent(true);
    try {
      const currentMatch = matches[currentMatchIndex];
      await Promise.resolve(onReplace(currentMatch, replaceTerm, searchTerm, effectiveOptions));

      // Re-run search to update matches
      const newMatches = onFind(searchTerm, effectiveOptions);
      setHasSearched(true);
      setSearchDirty(false);
      setMatches(newMatches);

      // Adjust current index if needed
      if (newMatches.length === 0) {
        setCurrentMatchIndex(-1);
        onHighlightMatch?.(null);
      } else if (currentMatchIndex >= newMatches.length) {
        setCurrentMatchIndex(newMatches.length - 1);
        onHighlightMatch?.(newMatches[newMatches.length - 1]);
      } else {
        onHighlightMatch?.(newMatches[currentMatchIndex]);
      }
    } finally {
      setIsReplacingCurrent(false);
    }
  }, [canRunSearch, currentMatchIndex, matches, replaceTerm, onReplace, searchTerm, effectiveOptions, onFind, onHighlightMatch, searchDirty, isReplacingCurrent]);

  // Handle replace all
  const handleReplaceAll = useCallback(async () => {
    if (!canRunSearch || isReplacingAll) return;

    setIsReplacingAll(true);
    try {
      const count = await Promise.resolve(onReplaceAll(searchTerm, replaceTerm, effectiveOptions));

      // Clear matches after replace all
      setMatches([]);
      setCurrentMatchIndex(-1);
      setHasSearched(false);
      setSearchDirty(false);
      onHighlightMatch?.(null);
      setReplaceCount(typeof count === 'number' ? count : null);
    } finally {
      setIsReplacingAll(false);
    }
  }, [canRunSearch, searchTerm, replaceTerm, effectiveOptions, onReplaceAll, onHighlightMatch, isReplacingAll]);

  // Handle close
  const handleClose = useCallback(() => {
    if (previewOnly) {
      return;
    }
    onHighlightMatch?.(null);
    onClose();
  }, [onClose, onHighlightMatch, previewOnly]);

  // Handle Enter key in search field
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFindNext();
    }
  }, [handleFindNext]);

  // Handle Enter key in replace field
  const handleReplaceKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') {
      return;
    }

    e.preventDefault();

    if (e.ctrlKey || e.metaKey) {
      void handleReplaceAll();
      return;
    }

    if (canReplaceCurrent) {
      void handleReplaceCurrent();
      return;
    }

    handleFindNext();
  }, [canReplaceCurrent, handleFindNext, handleReplaceAll, handleReplaceCurrent]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="find-replace-dialog-title"
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={previewOnly}
      slotProps={{ container: { sx: previewDockSx(previewOnly) } }}
      PaperProps={{
        sx: {
          background: theme.palette.background.paper,
          border: `1px solid ${surfaceBorder}`,
          borderRadius: `${borderRadius.lg}px`,
          boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.4)}`,
          overflow: 'hidden',
          m: 2,
          ...previewDockPaperSx(previewOnly),
          width: 'min(720px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 32px)',
        }
      }}
    >
      <FindReplaceDialogHeader previewOnly={previewOnly} onClose={handleClose} />

      <DialogContent
        ref={contentRef}
        data-testid="find-replace-dialog-content"
        sx={{
          px: 2,
          pt: 2.75,
          pb: 1.5,
          overflow: 'visible',
        }}
      >
        <Stack spacing={layoutGap.section} sx={{ overflow: 'visible' }}>
          {/* Find/Replace Inputs */}
          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              pt: 0.5,
              overflow: 'visible',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
            }}
          >
            <TextField
              fullWidth
              label="Find"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                invalidateResults();
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search…"
              size="small"
              autoFocus
              disabled={isReplacing}
              error={Boolean(regexError)}
              helperText={regexError ? 'Invalid pattern' : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
            <TextField
              fullWidth
              label="Replace"
              value={replaceTerm}
              onChange={(e) => {
                setReplaceTerm(e.target.value);
                setReplaceCount(null);
              }}
              onKeyDown={handleReplaceKeyDown}
              placeholder="Replace with…"
              size="small"
              disabled={isReplacing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {/* Options Row */}
          <Box
            sx={{
              display: 'grid',
              alignItems: 'center',
              gap: 1,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: options.scope === 'column'
                  ? 'minmax(118px, 0.7fr) minmax(132px, 0.9fr) minmax(196px, 1.1fr) auto'
                  : 'minmax(118px, 0.75fr) minmax(196px, 1.1fr) auto',
              },
              p: 1,
              borderRadius: `${borderRadius.md}px`,
              border: `1px solid ${surfaceBorder}`,
              backgroundColor: mutedSurface,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={options.scope}
                size="small"
                aria-label="Search scope"
                disabled={isReplacing}
                onChange={(e) => {
                  const nextScope = e.target.value as 'all' | 'column';
                  setOptions(prev => ({
                    ...prev,
                    scope: nextScope,
                    scopeColumn: nextScope === 'column'
                      ? (prev.scopeColumn && editableColumns.includes(prev.scopeColumn)
                          ? prev.scopeColumn
                          : editableColumns[0])
                      : prev.scopeColumn
                  }));
                  invalidateResults();
                }}
                sx={selectSx}
              >
                <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>All columns</MenuItem>
                <MenuItem value="column" disabled={editableColumns.length === 0} sx={{ fontSize: '0.8125rem' }}>Choose a column…</MenuItem>
              </Select>
            </FormControl>

            {options.scope === 'column' && (
              <FormControl size="small" sx={{ minWidth: 0 }}>
                <Select
                  value={effectiveOptions.scopeColumn || ''}
                  size="small"
                  aria-label="Column to search"
                  disabled={isReplacing}
                  onChange={(e) => {
                    setOptions(prev => ({ ...prev, scopeColumn: e.target.value }));
                    invalidateResults();
                  }}
                  sx={selectSx}
                >
                  {editableColumns.map(col => (
                    <MenuItem key={col} value={col} sx={{ fontSize: '0.8125rem' }}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <ToggleButtonGroup
              exclusive
              size="small"
              value={options.rowScope ?? 'all'}
              onChange={(_event, nextScope: FindReplaceOptions['rowScope'] | null) => {
                if (!nextScope) {
                  return;
                }

                setOptions(prev => ({
                  ...prev,
                  rowScope: nextScope,
                  rowIndices: undefined,
                }));
                invalidateResults();
              }}
              disabled={isReplacing}
              aria-label="Find and replace row scope"
              sx={{
                minWidth: 0,
                justifySelf: 'stretch',
                '& .MuiToggleButtonGroup-grouped': {
                  flex: 1,
                  minWidth: 0,
                  borderColor: controlBorderColor,
                  '&:not(:first-of-type)': {
                    borderLeftColor: controlBorderColor,
                  },
                },
              }}
            >
              <ToggleButton value="all" aria-label="Search all rows" sx={optionToggleSx}>
                All
              </ToggleButton>
              <ToggleButton value="visible" aria-label={`Search visible rows (${safeVisibleRowIndices.length})`} sx={optionToggleSx}>
                Visible {safeVisibleRowIndices.length}
              </ToggleButton>
              <ToggleButton
                value="selected"
                aria-label={`Search selected rows (${safeSelectedRowIndices.length})`}
                disabled={safeSelectedRowIndices.length === 0}
                sx={optionToggleSx}
              >
                Selected {safeSelectedRowIndices.length}
              </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              size="small"
              value={[
                ...(options.caseSensitive ? ['caseSensitive'] : []),
                ...(options.wholeCell ? ['wholeCell'] : []),
                ...(options.useRegularExpression ? ['regularExpression'] : []),
              ]}
              onChange={(_event, nextOptions: string[]) => {
                setOptions(prev => ({
                  ...prev,
                  caseSensitive: nextOptions.includes('caseSensitive'),
                  wholeCell: nextOptions.includes('wholeCell'),
                  useRegularExpression: nextOptions.includes('regularExpression'),
                }));
                invalidateResults();
              }}
              disabled={isReplacing}
              aria-label="Find and replace matching options"
              sx={{
                justifySelf: { xs: 'stretch', sm: 'end' },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiToggleButtonGroup-grouped': {
                  flex: { xs: 1, sm: '0 0 auto' },
                  borderColor: controlBorderColor,
                  '&:not(:first-of-type)': {
                    borderLeftColor: controlBorderColor,
                  },
                },
              }}
            >
              <ToggleButton value="caseSensitive" aria-label="Match case" sx={optionToggleSx}>
                Aa
              </ToggleButton>
              <ToggleButton value="wholeCell" aria-label="Match whole cell" sx={optionToggleSx}>
                Whole cell
              </ToggleButton>
              <ToggleButton value="regularExpression" aria-label="Use regular expression" sx={optionToggleSx}>
                .*
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Status & Navigation */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}>
            <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" useFlexGap flexWrap="wrap" role="status" aria-live="polite">
              <Chip
                label={statusText}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: statusColor,
                  backgroundColor: alpha(statusColor, 0.1),
                  border: `1px solid ${alpha(statusColor, 0.2)}`,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
              {currentLocationText && (
                <Chip
                  label={currentLocationText}
                  size="small"
                  sx={{
                    height: 24,
                    maxWidth: 220,
                    color: theme.palette.text.secondary,
                    backgroundColor: raisedSurface,
                    border: `1px solid ${surfaceBorder}`,
                    '& .MuiChip-label': {
                      px: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              )}
            </Stack>
            <Box sx={{ display: 'flex', gap: layoutGap.dense }}>
              <Tooltip title="Previous match" arrow>
                <span>
                  <IconButton
                    onClick={handleFindPrevious}
                    disabled={isReplacing || !hasActiveMatches}
                    aria-label="Previous match"
                    size="small"
                    sx={{
                      width: 26,
                      height: 26,
                      color: theme.palette.text.secondary,
                      '&:hover:not(:disabled)': { color: theme.palette.primary.main },
                    }}
                  >
                    <NavigateBeforeIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next match" arrow>
                <span>
                  <IconButton
                    onClick={handleFindNext}
                    disabled={isReplacing || !hasActiveMatches}
                    aria-label="Next match"
                    size="small"
                    sx={{
                      width: 26,
                      height: 26,
                      color: theme.palette.text.secondary,
                      '&:hover:not(:disabled)': { color: theme.palette.primary.main },
                    }}
                  >
                    <NavigateNextIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Matches List */}
          {hasActiveMatches ? (
            <Box
              sx={{
                maxHeight: 140,
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${surfaceBorder}`,
                backgroundColor: mutedSurface,
                overflow: 'auto',
              }}
            >
              <List dense disablePadding sx={{ p: 0.75 }}>
                {previewMatches.map(({ match, index }) => {
                  const previewValue = getMatchPreviewValue(match);
                  const previewChanged = previewValue !== match.value;

                  return (
                    <ListItemButton
                      key={`${match.row}-${match.column}-${index}`}
                      selected={index === currentMatchIndex}
                      aria-current={index === currentMatchIndex ? 'true' : undefined}
                      aria-label={`Match ${index + 1}: row ${match.row + 1}, ${match.column}, ${match.value || 'empty'}${index === currentMatchIndex ? ', current' : ''}`}
                      onClick={() => handleSelectMatch(index)}
                      sx={{
                        py: layoutGap.dense,
                        px: 1,
                        mb: layoutGap.dense,
                        borderRadius: `${borderRadius.sm}px`,
                        minHeight: 34,
                        '&:last-of-type': { mb: 0 },
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        },
                        '&:hover': {
                          backgroundColor: raisedSurface,
                        },
                      }}
                    >
                      <ListItemText
                        primary={(
                          <Box sx={{ display: 'grid', gridTemplateColumns: '45px minmax(72px, 120px) minmax(0, 1fr)', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              Row {match.row + 1}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 0.75,
                                py: layoutGap.micro,
                                borderRadius: `${borderRadius.sm}px`,
                                backgroundColor: alpha(theme.palette.common.white, 0.06),
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {match.column}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: layoutGap.dense, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: previewChanged ? 92 : 180,
                                }}
                              >
                                {match.value || '(empty)'}
                              </Typography>
                              {previewChanged ? (
                                <>
                                  <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.7), flexShrink: 0 }}>
                                    -&gt;
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.success.main,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 92,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {previewValue || '(empty)'}
                                  </Typography>
                                </>
                              ) : null}
                            </Box>
                          </Box>
                        )}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
              {hiddenMatchCount > 0 && (
                <Box sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: `1px solid ${surfaceBorder}`,
                  backgroundColor: alpha(theme.palette.common.white, 0.02),
                }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.6875rem' }}>
                    +{hiddenMatchCount} more match{hiddenMatchCount === 1 ? '' : 'es'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              py: 0.75,
              px: 1,
              borderRadius: `${borderRadius.md}px`,
              textAlign: 'center',
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: theme.palette.text.secondary }}>
                {replaceCount !== null
                  ? 'Replacement complete'
                  : searchDirty
                    ? 'Search changed. Press Enter or Find all to refresh.'
                    : 'No matches'}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <FindReplaceDialogActions
        previewOnly={previewOnly}
        isReplacing={isReplacing}
        canRunSearch={canRunSearch}
        canReplaceCurrent={canReplaceCurrent}
        canReplaceAll={canReplaceAll}
        isReplacingCurrent={isReplacingCurrent}
        isReplacingAll={isReplacingAll}
        onClose={handleClose}
        onFind={handleFind}
        onReplaceCurrent={handleReplaceCurrent}
        onReplaceAll={handleReplaceAll}
      />
    </Dialog>
  );
};

export default FindReplaceDialog;
